import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Freeze Frame — composant jeu principal (Go/NoGo inhibition).
 *
 * Pattern unifié `@labrain/game-shell` (DEC-005) :
 *   - `<GameShell>` orchestre xstate + bus + polish + boucle staircase.
 *   - `<PhaseRenderer>` route intro / demo / staircase / wrap_up / debrief.
 *   - `FreezeFrameAdapter` (Promise-based) gère sub-phases internes
 *     (fixation → stimulus → awaiting_response → feedback → iti).
 *   - `<DebriefShell>` fournit le squelette debrief partagé.
 *
 * Sprint 2 wire-up : level piloté par game-progression (localStorage), avec
 * sélecteur 1-50 dans l'intro et promotion du bestLevel sur staircase converged.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ActionBarProvider, ActionBarSlot, GameShell, PhaseRenderer, useActionBar, useGameShellFromContext, } from '@labrain/game-shell';
import { createFreezeFrameAdapter, makeStimulus, } from './adapter';
import { Demo } from './demo';
import { FFDebriefView, FFStaircaseView } from './staircaseView';
import { paradigmMeta, staircaseConfig, variantForLevel, } from './params';
import { LevelSelector } from '../../components/LevelSelector';
import { PedagogieDrawer } from '../../components/PedagogieDrawer';
import { loadProgression, promoteOnSuccess, setCurrentLevel, } from '../../lib/game-progression';
import { loadUserState } from '../../lib/user-state';
const GAME_SLUG = 'freeze-frame';
function Intro({ level, progression, onSelectLevel, onShowDemo, onSkipDemo, onWhy }) {
    const { t } = useTranslation(['games', 'common']);
    const variant = variantForLevel(level);
    useActionBar({
        primary: (_jsx("button", { type: "button", onClick: onShowDemo, className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: t('common:actions.see_demo') })),
        secondary: (_jsx("button", { type: "button", onClick: onSkipDemo, className: "inline-flex h-12 w-full items-center justify-center border border-cyan-border bg-cyan-surface font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-text transition active:scale-[0.985] hover:bg-cyan-bg", children: t('common:actions.start_direct') })),
    });
    return (_jsxs("div", { className: "flex flex-1 flex-col items-center gap-s5 py-s6 text-center", children: [_jsx("p", { className: "font-mono text-[9.5px] uppercase tracking-[0.30em] text-cyan-text-faint", children: paradigmMeta.paradigm }), _jsx("h1", { className: "font-display text-[28px] font-medium leading-9 tracking-cy-display text-cyan-text", children: t('freeze_frame.title') }), _jsx("p", { className: "max-w-[320px] text-[14.5px] leading-5 text-cyan-text-secondary", children: t('freeze_frame.intro_description') }), _jsxs("div", { className: "grid w-full grid-cols-2 gap-s3 border border-cyan-border-faint bg-cyan-surface p-s4 text-start text-[12px] text-cyan-text-secondary", children: [_jsxs("div", { children: [_jsx("span", { className: "block font-mono uppercase tracking-[0.20em] text-cyan-text-faint", children: t('freeze_frame.fields.level') }), _jsx("span", { className: "text-[14px] text-cyan-text", children: t('freeze_frame.level_with_label', { level, label: variant.label }) })] }), _jsxs("div", { children: [_jsx("span", { className: "block font-mono uppercase tracking-[0.20em] text-cyan-text-faint", children: t('common:metrics.trials') }), _jsx("span", { className: "text-[14px] text-cyan-text", children: t('common:labels.trials_max', { n: staircaseConfig.maxTrials }) })] })] }), _jsx(LevelSelector, { gameSlug: GAME_SLUG, bestLevel: progression.bestLevel, currentLevel: progression.currentLevel, onSelect: onSelectLevel }), _jsx("button", { type: "button", onClick: onWhy, className: "text-[12px] text-cyan-network underline-offset-4 hover:underline", children: t('common:actions.why_this_game') }), _jsx(Link, { to: "/library", className: "text-[12px] text-cyan-text-secondary underline-offset-4 hover:underline", children: t('common:actions.back_to_play') })] }));
}
function IntroSlot({ level, progression, onSelectLevel }) {
    const shell = useGameShellFromContext();
    const [pedagogieOpen, setPedagogieOpen] = useState(false);
    return (_jsxs(_Fragment, { children: [_jsx(Intro, { level: level, progression: progression, onSelectLevel: onSelectLevel, onShowDemo: () => shell.startSession(), onSkipDemo: () => shell.skipDemo(), onWhy: () => setPedagogieOpen(true) }), _jsx(PedagogieDrawer, { gameSlug: GAME_SLUG, isOpen: pedagogieOpen, onClose: () => setPedagogieOpen(false) })] }));
}
function DemoSlot({ level }) {
    const shell = useGameShellFromContext();
    return (_jsx(Demo, { level: level, onComplete: () => shell.markDemoDone(), onSkip: () => shell.markDemoDone() }));
}
function WrapUpSlot({ onCompleted }) {
    const shell = useGameShellFromContext();
    useMemo(() => {
        const threshold = shell.snapshot.staircase.thresholdEstimate;
        try {
            window.localStorage.setItem('labrain.freeze-frame.lastRun', JSON.stringify({
                ts: Date.now(),
                threshold,
            }));
        }
        catch {
            /* ignored */
        }
        onCompleted(threshold);
        shell.markWrapUpDone();
        return null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return null;
}
export function FreezeFrameGame() {
    const navigate = useNavigate();
    const [progression, setProgression] = useState(() => loadProgression(GAME_SLUG));
    const level = progression.currentLevel;
    const levelRef = useRef(level);
    levelRef.current = level;
    const adapterRef = useRef(null);
    if (adapterRef.current === null) {
        adapterRef.current = createFreezeFrameAdapter(level);
    }
    const adapter = adapterRef.current;
    useEffect(() => {
        adapter.setLevel(level);
    }, [adapter, level]);
    const handleSelectLevel = (newLevel) => {
        setProgression(setCurrentLevel(GAME_SLUG, newLevel));
    };
    const handleCompleted = (threshold) => {
        if (threshold !== null && Number.isFinite(threshold)) {
            setProgression(promoteOnSuccess(GAME_SLUG, levelRef.current, threshold));
        }
    };
    const userSettings = useMemo(() => loadUserState().settings, []);
    const config = useMemo(() => ({
        gameSlug: GAME_SLUG,
        gameMaster: 'air',
        staircaseConfig,
        showDemo: true,
        showCalibrationWarmup: false,
        enableHaptics: userSettings.enableHaptics,
        enableSound: userSettings.enableSound,
        enableVisualFeedback: true,
    }), [userSettings.enableHaptics, userSettings.enableSound]);
    return (_jsxs(ActionBarProvider, { children: [_jsx(GameShell, { config: config, adapter: adapter, buildStimulus: (engStim) => makeStimulus(engStim.trialIdx, engStim.value, levelRef.current), grader: (_stim, resp) => ({
                    correct: resp.correct,
                    reactionTimeMs: resp.reactionTimeMs,
                    rawInput: { tapped: resp.tapped, wasGo: resp.wasGo },
                }), children: _jsx(PhaseRenderer, { intro: _jsx(IntroSlot, { level: level, progression: progression, onSelectLevel: handleSelectLevel }), demo: _jsx(DemoSlot, { level: level }), staircase: _jsx(FFStaircaseView, { adapter: adapter, level: level, onAbort: () => navigate('/library') }), wrapUp: _jsx(WrapUpSlot, { onCompleted: handleCompleted }), debrief: _jsx(FFDebriefView, { level: level, onAbort: () => navigate('/library') }), aborted: null }) }), _jsx(ActionBarSlot, {})] }));
}
export default FreezeFrameGame;
//# sourceMappingURL=game.js.map